import React, { useCallback, useEffect, useState } from "react";
import { callLimitsAPI } from "../../services/api/admin";

const POLL_INTERVAL_MS = 10000;

// The four vendor components every voice call consumes. A call reserves one lane
// per component and may start only if every one has headroom.
const COMPONENTS = [
  { key: "tts", label: "TTS (speech)" },
  { key: "asr", label: "ASR (transcription)" },
  { key: "llm", label: "LLM" },
  { key: "telephony", label: "Telephony" },
];
const COMPONENT_ORDER = { tts: 0, asr: 1, llm: 2, telephony: 3, system: 4 };

// Split a "<component>:<vendor>" scope. Non-component scopes (global, stress_test,
// ci_<id>) have no component and sort into the "system" group.
function parseScope(scope) {
  const s = String(scope || "");
  const idx = s.indexOf(":");
  if (idx === -1) return { component: "system", vendor: s };
  return { component: s.slice(0, idx), vendor: s.slice(idx + 1) };
}

function pct(used, cap) {
  if (!cap || cap <= 0) return 0;
  const p = (Number(used || 0) / Number(cap)) * 100;
  if (!isFinite(p) || p < 0) return 0;
  return Math.min(p, 100);
}

function barColor(p) {
  if (p >= 90) return "bg-red-500";
  if (p >= 70) return "bg-yellow-500";
  return "bg-green-500";
}

function InFlightBar({ used, cap }) {
  const p = pct(used, cap);
  return (
    <div className="min-w-[140px]">
      <div className="flex items-center justify-between text-xs text-muted-foreground mb-1">
        <span>in flight</span>
        <span>
          {used ?? 0}/{cap ?? "—"}
        </span>
      </div>
      <div className="w-full h-2 rounded-full bg-muted overflow-hidden">
        <div className={`h-full rounded-full ${barColor(p)}`} style={{ width: `${p}%` }} />
      </div>
    </div>
  );
}

export default function AdminCallLimits() {
  const [rows, setRows] = useState([]);
  const [effective, setEffective] = useState(null);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState({ type: "", text: "" });
  const [editingId, setEditingId] = useState(null);
  const [editValue, setEditValue] = useState("");
  const [saving, setSaving] = useState({});
  const [newComponent, setNewComponent] = useState("tts");
  const [newVendor, setNewVendor] = useState("");
  const [newLimit, setNewLimit] = useState("");

  const showMsg = (type, text) => {
    setMsg({ type, text });
    if (text) setTimeout(() => setMsg({ type: "", text: "" }), 4000);
  };

  const load = useCallback(async (showLoader = false) => {
    if (showLoader) setLoading(true);
    try {
      const data = await callLimitsAPI.list();
      setRows(data.limits || []);
      setEffective(data.effective || null);
    } catch (e) {
      showMsg("error", "Failed to load call limits");
    } finally {
      if (showLoader) setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(true);
    const t = setInterval(() => load(false), POLL_INTERVAL_MS);
    return () => clearInterval(t);
  }, [load]);

  const saveEdit = async (row) => {
    const max_concurrent = parseInt(editValue, 10);
    if (!max_concurrent || max_concurrent < 1) {
      showMsg("error", "Limit must be at least 1");
      return;
    }
    setSaving((s) => ({ ...s, [row.id]: true }));
    try {
      if (row.id == null) {
        // Virtual row (the default stress_test lane) isn't persisted yet, so
        // PATCH /limits/null 404s. Create it instead; `load()` then re-fetches
        // it with a real id so future edits PATCH normally.
        await callLimitsAPI.create({ scope: row.scope, max_concurrent });
      } else {
        await callLimitsAPI.update(row.id, { max_concurrent });
      }
      showMsg("success", `${row.scope} limit set to ${max_concurrent}`);
      setEditingId(null);
      await load();
    } catch (e) {
      showMsg("error", e.response?.data?.error || "Failed to update limit");
    } finally {
      setSaving((s) => ({ ...s, [row.id]: false }));
    }
  };

  const toggleEnabled = async (row) => {
    setSaving((s) => ({ ...s, [row.id]: true }));
    try {
      await callLimitsAPI.update(row.id, { enabled: !row.enabled });
      await load();
    } catch (e) {
      showMsg("error", e.response?.data?.error || "Failed to update limit");
    } finally {
      setSaving((s) => ({ ...s, [row.id]: false }));
    }
  };

  const addVendor = async () => {
    const vendor = newVendor.trim().toLowerCase();
    const max_concurrent = parseInt(newLimit, 10);
    if (!vendor || !max_concurrent || max_concurrent < 1) {
      showMsg("error", "Pick a component, enter a vendor, and a limit of at least 1");
      return;
    }
    const scope = `${newComponent}:${vendor}`;
    try {
      await callLimitsAPI.create({ scope, max_concurrent });
      showMsg("success", `Added ${scope} limit`);
      setNewVendor("");
      setNewLimit("");
      await load();
    } catch (e) {
      showMsg("error", e.response?.data?.error || "Failed to add vendor limit");
    }
  };

  const removeVendor = async (row) => {
    if (!window.confirm(`Remove the ${row.scope} limit? Its calls will then be bounded only by the global ceiling.`)) return;
    try {
      await callLimitsAPI.remove(row.id);
      showMsg("success", `Removed ${row.scope} limit`);
      await load();
    } catch (e) {
      showMsg("error", e.response?.data?.error || "Failed to remove limit");
    }
  };

  if (loading) return <div className="p-6 text-sm text-muted-foreground">Loading call limits…</div>;

  // Attach parsed component/vendor and sort by component, then vendor.
  const decorated = rows
    .map((r) => ({ ...r, ...parseScope(r.scope) }))
    .sort((a, b) => {
      const ca = COMPONENT_ORDER[a.component] ?? 9;
      const cb = COMPONENT_ORDER[b.component] ?? 9;
      if (ca !== cb) return ca - cb;
      return String(a.vendor).localeCompare(String(b.vendor));
    });

  const componentLabel = (key) =>
    COMPONENTS.find((c) => c.key === key)?.label || (key === "system" ? "System" : key);

  return (
    <div className="p-6 space-y-5">
      <div>
        <h1 className="text-lg font-semibold text-foreground">Call Concurrency</h1>
        <p className="text-sm text-muted-foreground mt-1 max-w-3xl">
          Per-vendor limits for the four components every call uses. Changes apply immediately —
          no deploy, no restart.
        </p>
      </div>

      {msg.text && (
        <div
          className={`rounded-md px-3 py-2 text-sm ${
            msg.type === "error"
              ? "bg-red-50 text-red-700 border border-red-200"
              : "bg-green-50 text-green-700 border border-green-200"
          }`}
        >
          {msg.text}
        </div>
      )}

      <div className="rounded-lg border border-border bg-card p-4 text-sm text-muted-foreground space-y-1">
        <p>
          Every call uses one vendor per component (<span className="font-medium text-foreground">TTS</span>,{" "}
          <span className="font-medium text-foreground">ASR</span>,{" "}
          <span className="font-medium text-foreground">LLM</span>,{" "}
          <span className="font-medium text-foreground">Telephony</span>). A call may start only when
          <em> every</em> one of its vendors has headroom — otherwise it queues. So Cartesia's limit caps
          only calls whose TTS is Cartesia; a call on a different TTS vendor is bound by that vendor instead.
        </p>
        <p>
          <span className="font-medium text-foreground">Global</span> is the box ceiling on top of all of these.
        </p>
        {effective && (
          <p className="pt-1">
            Currently enforcing a global ceiling of{" "}
            <span className="font-medium text-foreground">{effective.global}</span> with{" "}
            <span className="font-medium text-foreground">{effective.in_flight}</span> call(s) in flight.
          </p>
        )}
      </div>

      <div className="rounded-lg border border-border overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted/50">
            <tr className="text-left text-xs uppercase tracking-wide text-muted-foreground">
              <th className="px-4 py-2 font-medium">Component</th>
              <th className="px-4 py-2 font-medium">Vendor</th>
              <th className="px-4 py-2 font-medium">Max concurrent</th>
              <th className="px-4 py-2 font-medium">Usage</th>
              <th className="px-4 py-2 font-medium">Enabled</th>
              <th className="px-4 py-2 font-medium text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {decorated.map((row, i) => {
              const isSystem = row.component === "system";
              const busy = !!saving[row.id];
              // First row of each component group shows the component label.
              const firstOfGroup = i === 0 || decorated[i - 1].component !== row.component;
              return (
                <tr key={row.id ?? row.scope} className="border-t border-border">
                  <td className="px-4 py-3">
                    {firstOfGroup ? (
                      <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                        {componentLabel(row.component)}
                      </span>
                    ) : null}
                  </td>
                  <td className="px-4 py-3">
                    <span className="font-medium text-foreground">{row.vendor}</span>
                  </td>
                  <td className="px-4 py-3">
                    {editingId === row.id ? (
                      <div className="flex items-center gap-2">
                        <input
                          type="number"
                          min="1"
                          value={editValue}
                          onChange={(e) => setEditValue(e.target.value)}
                          className="w-20 rounded border border-input px-2 py-1 text-sm"
                        />
                        <button
                          onClick={() => saveEdit(row)}
                          disabled={busy}
                          className="rounded bg-primary px-2 py-1 text-xs text-primary-foreground disabled:opacity-50"
                        >
                          {busy ? "Saving…" : "Save"}
                        </button>
                        <button
                          onClick={() => setEditingId(null)}
                          className="rounded border border-input px-2 py-1 text-xs"
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <span className="font-mono text-foreground">{row.max_concurrent}</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <InFlightBar used={row.in_flight} cap={row.max_concurrent} />
                  </td>
                  <td className="px-4 py-3">
                    <button
                      onClick={() => toggleEnabled(row)}
                      disabled={busy || row.id == null}
                      className={`rounded-full px-2 py-0.5 text-xs font-medium ${
                        row.enabled
                          ? "bg-green-100 text-green-700"
                          : "bg-muted text-muted-foreground"
                      }`}
                      title={
                        row.enabled
                          ? "Disable — falls back to the deploy-time default"
                          : "Enable this limit"
                      }
                    >
                      {row.enabled ? "enabled" : "disabled"}
                    </button>
                  </td>
                  <td className="px-4 py-3 text-right space-x-2">
                    {editingId !== row.id && (
                      <button
                        onClick={() => {
                          setEditingId(row.id);
                          setEditValue(String(row.max_concurrent));
                        }}
                        className="text-xs underline text-muted-foreground hover:text-foreground"
                      >
                        Edit
                      </button>
                    )}
                    {!isSystem && row.id != null && (
                      <button
                        onClick={() => removeVendor(row)}
                        className="text-xs underline text-red-600 hover:text-red-700"
                      >
                        Remove
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="rounded-lg border border-border bg-card p-4">
        <h2 className="text-sm font-medium text-foreground mb-2">Add a vendor limit</h2>
        <p className="text-xs text-muted-foreground mb-3">
          Pick the component and enter the vendor name exactly as configured on the agent (e.g.{" "}
          <code>cartesia</code>, <code>deepgram</code>, <code>openai</code>, <code>twilio</code>). Only
          calls using that vendor for that component are capped by it.
        </p>
        <div className="flex flex-wrap items-center gap-2">
          <select
            value={newComponent}
            onChange={(e) => setNewComponent(e.target.value)}
            className="rounded border border-input px-2 py-1 text-sm"
          >
            {COMPONENTS.map((c) => (
              <option key={c.key} value={c.key}>
                {c.label}
              </option>
            ))}
          </select>
          <input
            placeholder="vendor (e.g. cartesia)"
            value={newVendor}
            onChange={(e) => setNewVendor(e.target.value)}
            className="rounded border border-input px-2 py-1 text-sm"
          />
          <input
            type="number"
            min="1"
            placeholder="limit"
            value={newLimit}
            onChange={(e) => setNewLimit(e.target.value)}
            className="w-24 rounded border border-input px-2 py-1 text-sm"
          />
          <button
            onClick={addVendor}
            className="rounded bg-primary px-3 py-1 text-sm text-primary-foreground"
          >
            Add
          </button>
        </div>
      </div>
    </div>
  );
}
